import { TimePicker } from '@components/TimePicker';
import {
  Box,
  Checkbox,
  Grid,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  FacilityHoursValues,
  FacilityOperatingHours,
  operatingTimeForPicker,
  operatingTimeFromPicker,
} from '@common/facilityDetails';
import { Index, Show } from 'solid-js';

const days = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

type WeeklyOperatingHoursEditorProps<T extends FacilityHoursValues> = {
  facilityDetails: () => T;
  onChange: (facilityDetails: T) => void;
  dataTestIdPrefix: string;
  disabled?: boolean;
};

export const WeeklyOperatingHoursEditor = <T extends FacilityHoursValues>(
  props: WeeklyOperatingHoursEditorProps<T>,
) => {
  const updateDay = (
    schedule: 'pickupHours' | 'receivingHours',
    dayOfWeek: number,
    updates: Partial<FacilityOperatingHours>,
  ) => {
    if (props.disabled) return;
    const value = props.facilityDetails();
    props.onChange({
      ...value,
      [schedule]: value[schedule].map((day) =>
        day.dayOfWeek === dayOfWeek ? { ...day, ...updates } : day,
      ),
    });
  };

  const applyMonday = (
    schedule: 'pickupHours' | 'receivingHours',
    target: 'all' | 'weekdays',
  ) => {
    if (props.disabled) return;
    const value = props.facilityDetails();
    const monday = value[schedule].find((day) => day.dayOfWeek === 1);
    if (!monday) return;
    props.onChange({
      ...value,
      [schedule]: value[schedule].map((day) => {
        if (target === 'weekdays' && day.dayOfWeek > 5) return day;
        return {
          dayOfWeek: day.dayOfWeek,
          openTime: monday.openTime,
          closeTime: monday.closeTime,
          isClosed: monday.isClosed,
        };
      }),
    });
  };

  const renderSchedule = (
    schedule: 'pickupHours' | 'receivingHours',
    label: string,
  ) => (
    <Grid item size={12}>
      <div class="flex items-center justify-between">
        <Typography variant="body2" class="font-bold! text-[#123b50]!">
          {label}
        </Typography>
        <Show when={schedule === 'receivingHours'}>
          <Checkbox
            label="Use pickup hours"
            checked={props.facilityDetails().receivingUsesPickupHours}
            disabled={props.disabled}
            onChange={(value: boolean) => {
              if (props.disabled) return;
              props.onChange({
                ...props.facilityDetails(),
                receivingUsesPickupHours: value,
              });
            }}
            dataTestId={`${props.dataTestIdPrefix}-receiving-uses-pickup`}
          />
        </Show>
      </div>
      <Show
        when={
          schedule !== 'receivingHours' ||
          !props.facilityDetails().receivingUsesPickupHours
        }
      >
        <Index each={props.facilityDetails()[schedule]}>
          {(day) => (
            <Grid
              container
              spacing={1}
              class="items-center border-b border-gray-200 py-2"
            >
              <Grid item size={{ xs: 12, sm: 3 }}>
                <Typography variant="body2">
                  {days[day().dayOfWeek - 1]}
                </Typography>
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <TimePicker
                  label="From"
                  disabled={props.disabled || day().isClosed}
                  value={operatingTimeForPicker(day().openTime)}
                  onChange={(value) =>
                    updateDay(schedule, day().dayOfWeek, {
                      openTime: operatingTimeFromPicker(value),
                    })
                  }
                  dataTestId={`${props.dataTestIdPrefix}-${schedule}-${day().dayOfWeek}-from`}
                />
              </Grid>
              <Grid item size={{ xs: 6, sm: 3 }}>
                <TimePicker
                  label="To"
                  disabled={props.disabled || day().isClosed}
                  value={operatingTimeForPicker(day().closeTime)}
                  onChange={(value) =>
                    updateDay(schedule, day().dayOfWeek, {
                      closeTime: operatingTimeFromPicker(value),
                    })
                  }
                  dataTestId={`${props.dataTestIdPrefix}-${schedule}-${day().dayOfWeek}-to`}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 3 }}>
                <Checkbox
                  label="Closed"
                  checked={day().isClosed}
                  disabled={props.disabled}
                  onChange={(value: boolean) =>
                    updateDay(
                      schedule,
                      day().dayOfWeek,
                      value
                        ? { isClosed: value, openTime: '', closeTime: '' }
                        : { isClosed: value },
                    )
                  }
                  dataTestId={`${props.dataTestIdPrefix}-${schedule}-${day().dayOfWeek}-closed`}
                />
              </Grid>
            </Grid>
          )}
        </Index>
        <Box class="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          <button
            type="button"
            class="text-primary underline disabled:cursor-not-allowed disabled:opacity-50"
            disabled={props.disabled}
            onClick={() => applyMonday(schedule, 'all')}
            data-testid={`${props.dataTestIdPrefix}-${schedule}-apply-all`}
          >
            Apply Monday to all days
          </button>
          <button
            type="button"
            class="text-primary underline disabled:cursor-not-allowed disabled:opacity-50"
            disabled={props.disabled}
            onClick={() => applyMonday(schedule, 'weekdays')}
            data-testid={`${props.dataTestIdPrefix}-${schedule}-apply-weekdays`}
          >
            Apply Monday to weekdays
          </button>
        </Box>
      </Show>
    </Grid>
  );

  return (
    <Grid container spacing={2} rowSpacing={2}>
      <Grid item size={12}>
        <Box class="border border-[#b8d9e8] bg-[#eef8fc] px-3 py-2 text-sm text-[#123b50]">
          Operating hours are for information only and do not enforce pickup or
          receiving availability.
        </Box>
      </Grid>
      {renderSchedule('pickupHours', 'Pickup Hours')}
      <Grid item size={12}>
        <TextField
          label="Pickup Notes"
          value={props.facilityDetails().pickupHoursNotes}
          disabled={props.disabled}
          onChange={(value: string) => {
            if (props.disabled) return;
            props.onChange({
              ...props.facilityDetails(),
              pickupHoursNotes: value,
            });
          }}
          maxLength={500}
          dataTestId={`${props.dataTestIdPrefix}-pickup-notes`}
        />
      </Grid>
      {renderSchedule('receivingHours', 'Receiving Hours')}
      <Grid item size={12}>
        <TextField
          label="Receiving Notes"
          value={props.facilityDetails().receivingHoursNotes}
          disabled={props.disabled}
          onChange={(value: string) => {
            if (props.disabled) return;
            props.onChange({
              ...props.facilityDetails(),
              receivingHoursNotes: value,
            });
          }}
          maxLength={500}
          dataTestId={`${props.dataTestIdPrefix}-receiving-notes`}
        />
      </Grid>
    </Grid>
  );
};
